/**
 * 积分状态管理 Store
 * 管理用户积分余额（交易流水已改为 Dashboard / fund-transactions 等接口，不在此持久化）
 */

import { create } from 'zustand';
import type { CreditBalance } from '@/api';
import { getCreditBalance } from '@/api';

export interface UserCreditsData {
  balance: CreditBalance | null;
  lastUpdated: number | null;
}

export interface CreditsState {
  currentUserAddress: string | null;
  usersData: Record<string, UserCreditsData>;
  isLoadingBalance: boolean;

  setCurrentUserAddress: (address: string) => void;
  getCurrentUserData: () => UserCreditsData;
  setBalance: (balance: CreditBalance) => void;
  updateBalance: (updates: Partial<CreditBalance>) => void;
  setBalanceLoading: (loading: boolean) => void;
  clearCurrentUser: () => void;
  clear: () => void;
  refreshBalance: () => Promise<void>;
}

const initialState = {
  currentUserAddress: null,
  usersData: {},
  isLoadingBalance: false,
};

const getEmptyUserData = (): UserCreditsData => ({
  balance: null,
  lastUpdated: null,
});

export const useCreditsStore = create<CreditsState>()((set, get) => ({
  ...initialState,

  setCurrentUserAddress: (address) => {
    const normalizedAddress = address.toLowerCase();
    set((state) => {
      if (!state.usersData[normalizedAddress]) {
        return {
          currentUserAddress: normalizedAddress,
          usersData: {
            ...state.usersData,
            [normalizedAddress]: getEmptyUserData(),
          },
        };
      }
      return {
        currentUserAddress: normalizedAddress,
      };
    });
  },

  getCurrentUserData: () => {
    const state = get();
    if (!state.currentUserAddress) {
      return getEmptyUserData();
    }
    return state.usersData[state.currentUserAddress] || getEmptyUserData();
  },

  setBalance: (balance) => {
    set((state) => {
      if (!state.currentUserAddress) {
        console.warn('[Credits Store] No current user address set');
        return state;
      }
      return {
        usersData: {
          ...state.usersData,
          [state.currentUserAddress]: {
            ...state.usersData[state.currentUserAddress],
            balance,
            lastUpdated: Date.now(),
          },
        },
      };
    });
  },

  updateBalance: (updates) => {
    set((state) => {
      if (!state.currentUserAddress) {
        console.warn('[Credits Store] No current user address set');
        return state;
      }
      const currentUserData = state.usersData[state.currentUserAddress];
      return {
        usersData: {
          ...state.usersData,
          [state.currentUserAddress]: {
            ...currentUserData,
            balance: currentUserData.balance
              ? { ...currentUserData.balance, ...updates }
              : null,
            lastUpdated: Date.now(),
          },
        },
      };
    });
  },

  setBalanceLoading: (loading) => {
    set({ isLoadingBalance: loading });
  },

  clearCurrentUser: () => {
    set((state) => {
      if (!state.currentUserAddress) {
        return state;
      }
      return {
        usersData: {
          ...state.usersData,
          [state.currentUserAddress]: getEmptyUserData(),
        },
      };
    });
  },

  clear: () => {
    set(initialState);
  },

  refreshBalance: async () => {
    const state = get();
    if (!state.currentUserAddress) {
      console.warn('[Credits Store] Cannot refresh balance: no current user address');
      return;
    }

    set({ isLoadingBalance: true });
    try {
      const result = await getCreditBalance();
      if (result.ok) {
        set((currentState) => ({
          usersData: {
            ...currentState.usersData,
            [currentState.currentUserAddress!]: {
              ...currentState.usersData[currentState.currentUserAddress!],
              balance: result.data,
              lastUpdated: Date.now(),
            },
          },
        }));
      }
    } catch (error) {
      console.error('[Credits Store] Failed to refresh balance:', error);
    } finally {
      set({ isLoadingBalance: false });
    }
  },
}));

export const useBalance = () =>
  useCreditsStore((state) => {
    if (!state.currentUserAddress) return null;
    return state.usersData[state.currentUserAddress]?.balance || null;
  });

export const useAvailableCredits = () =>
  useCreditsStore((state) => {
    if (!state.currentUserAddress) return 0;
    return state.usersData[state.currentUserAddress]?.balance?.available || 0;
  });

export const useBalanceLoading = () => useCreditsStore((state) => state.isLoadingBalance);

export const useCurrentUserAddress = () =>
  useCreditsStore((state) => state.currentUserAddress);
